import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Grid, Card, CardContent, Avatar, Alert, IconButton, Divider, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import LoginIcon from '@mui/icons-material/Login';
import EmailIcon from '@mui/icons-material/Email';
import { designTokens } from '../theme/designTokens';
import EmailSettingsTab from '../components/organization/EmailSettingsTab';

interface Organization {
    _id: string;
    name: string;
    subdomain: string;
    logoUrl?: string;
    link?: string;
    theme?: {
        primaryColor?: string;
        secondaryColor?: string;
    };
    loginScreen?: {
        logoUrl?: string;
        backgroundImageUrl?: string;
        primaryColor?: string;
        secondaryColor?: string;
    };
    emailSettings?: {
        logoUrl?: string;
        headerType?: 'color' | 'image';
        headerValue?: string;
        colors?: {
            headerText?: string;
            title?: string;
            button?: string;
            buttonText?: string;
        };
        footer?: {
            text?: string;
            showSocialLinks?: boolean;
        };
    };
}

const LearnerPreview = ({ logoUrl, primaryColor, secondaryColor, orgName }: { logoUrl: string, primaryColor: string, secondaryColor: string, orgName: string }) => (
    <Card sx={{ ...designTokens.glassCard, height: '100%' }}>
        <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                Aperçu Espace Apprenant
            </Typography>
            <Box sx={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#f8fafc',
                height: 600,
                width: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }}>

                {/* Content */}
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Mock Header */}
                    <Box sx={{
                        bgcolor: primaryColor,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        transition: 'background-color 0.3s ease'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {logoUrl ? (
                                <img src={logoUrl} alt="Logo" style={{ height: 32, objectFit: 'contain', backgroundColor: 'white', borderRadius: 4, padding: '4px' }} />
                            ) : (
                                <BusinessIcon />
                            )}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{orgName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%' }} />
                            <Box sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%' }} />
                        </Box>
                    </Box>

                    {/* Mock Content */}
                    <Box sx={{ p: 3, display: 'flex', gap: 3, flex: 1 }}>
                        {/* Sidebar */}
                        <Box sx={{ width: 80, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 2, backdropFilter: 'blur(10px)' }}>
                            <Box sx={{ height: 10, width: '80%', bgcolor: secondaryColor, borderRadius: 1, opacity: 0.8, transition: 'background-color 0.3s ease' }} />
                            <Box sx={{ height: 10, width: '60%', bgcolor: 'grey.300', borderRadius: 1 }} />
                            <Box sx={{ height: 10, width: '70%', bgcolor: 'grey.300', borderRadius: 1 }} />
                        </Box>
                        {/* Main Area */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ height: 150, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2, p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', backdropFilter: 'blur(10px)' }}>
                                <Typography variant="subtitle2" sx={{ color: secondaryColor, fontWeight: 'bold', mb: 1, transition: 'color 0.3s ease' }}>Mon Parcours</Typography>
                                <Box sx={{ height: 10, width: '40%', bgcolor: 'grey.300', borderRadius: 1, mb: 2 }} />
                                <Box sx={{ height: 10, width: '90%', bgcolor: 'grey.200', borderRadius: 1 }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box sx={{ flex: 1, height: 100, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', backdropFilter: 'blur(10px)' }} />
                                <Box sx={{ flex: 1, height: 100, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', backdropFilter: 'blur(10px)' }} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const LoginPreview = ({ logoUrl, backgroundImageUrl, primaryColor, secondaryColor, orgName }: { logoUrl: string, backgroundImageUrl: string, primaryColor: string, secondaryColor: string, orgName: string }) => (
    <Card sx={{ ...designTokens.glassCard, height: '100%' }}>
        <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                Aperçu Écran de Connexion
            </Typography>
            <Box sx={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#f8fafc',
                height: 600,
                width: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                {/* Overlay if image */}
                {backgroundImageUrl && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        backdropFilter: 'blur(4px)'
                    }} />
                )}

                {/* Login Card */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: 350,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 4,
                    p: 4,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <Box sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        boxShadow: `0 4px 12px ${primaryColor}40`
                    }}>
                        {logoUrl ? (
                            <img src={logoUrl} alt="Logo" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '50%' }} />
                        ) : (
                            <LoginIcon sx={{ color: 'white' }} />
                        )}
                    </Box>
                    <Typography variant="h5" sx={{
                        fontWeight: 'bold',
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}>
                        Bienvenue
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Connectez-vous à {orgName}
                    </Typography>

                    <Box sx={{ width: '100%', height: 40, border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }} />
                    <Box sx={{ width: '100%', height: 40, border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }} />

                    <Box sx={{
                        width: '100%',
                        height: 40,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        boxShadow: `0 4px 12px ${primaryColor}40`
                    }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const MyOFPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);

    // General Tab State
    const [formData, setFormData] = useState({
        logoUrl: '',
        link: '',
        primaryColor: '#3b82f6',
        secondaryColor: '#1e40af'
    });

    // Connection Tab State
    const [loginFormData, setLoginFormData] = useState({
        logoUrl: '',
        backgroundImageUrl: '',
        primaryColor: '#667eea',
        secondaryColor: '#764ba2'
    });

    // Email Tab State
    const [emailSettings, setEmailSettings] = useState<any>({
        logoUrl: '',
        headerType: 'color',
        headerValue: '#667eea',
        colors: {
            headerText: '#ffffff',
            title: '#333333',
            button: '#667eea',
            buttonText: '#ffffff'
        },
        footer: {
            text: '',
            showSocialLinks: false
        }
    });

    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        fetchOrganization();
    }, []);

    const fetchOrganization = async () => {
        try {
            const res = await api.get('/api/organizations/my/organization');
            setOrganization(res.data);

            // Set General Data
            setFormData({
                logoUrl: res.data.logoUrl || '',
                link: res.data.link || '',
                primaryColor: res.data.theme?.primaryColor || '#3b82f6',
                secondaryColor: res.data.theme?.secondaryColor || '#1e40af'
            });

            // Set Login Screen Data
            setLoginFormData({
                logoUrl: res.data.loginScreen?.logoUrl || '',
                backgroundImageUrl: res.data.loginScreen?.backgroundImageUrl || '',
                primaryColor: res.data.loginScreen?.primaryColor || '#667eea',
                secondaryColor: res.data.loginScreen?.secondaryColor || '#764ba2'
            });

            // Set Email Settings
            if (res.data.emailSettings) {
                setEmailSettings(res.data.emailSettings);
            }

            setLoading(false);
        } catch (err) {
            console.error('Error fetching organization:', err);
            setLoading(false);
        }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);
    const loginLogoInputRef = useRef<HTMLInputElement>(null);
    const loginBgInputRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLoginFormData({ ...loginFormData, [e.target.name]: e.target.value });
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, logoUrl: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLoginLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLoginFormData(prev => ({ ...prev, logoUrl: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLoginBgUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLoginFormData(prev => ({ ...prev, backgroundImageUrl: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        try {
            const res = await api.put('/api/organizations/my/organization', {
                logoUrl: formData.logoUrl,
                link: formData.link,
                theme: {
                    primaryColor: formData.primaryColor,
                    secondaryColor: formData.secondaryColor
                },
                loginScreen: {
                    primaryColor: loginFormData.primaryColor,
                    secondaryColor: loginFormData.secondaryColor
                },
                emailSettings: emailSettings
            });
            setOrganization(res.data);
            setSaveMessage({ type: 'success', text: 'Paramètres de l\'organisme mis à jour avec succès !' });
            setTimeout(() => setSaveMessage(null), 5000);

            // Reload page to update logo in header
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            console.error('Error updating organization:', err);
            setSaveMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
            setTimeout(() => setSaveMessage(null), 5000);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography>Chargement...</Typography>
            </Box>
        );
    }

    if (!organization) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    Aucun organisme de formation associé à votre compte.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, maxWidth: '100%', mx: 'auto', position: 'relative', minHeight: '100vh' }}>
            {/* Background Image with Blur */}
            <Box sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'url(/bg-dark.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: -1,
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                }} />
            </Box>
            <Box sx={designTokens.pageHeader}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box>
                        <Typography variant="h4" sx={designTokens.pageTitle}>
                            <BusinessIcon fontSize="large" sx={{ color: "#2196F3" }} /> Mon Organisme de Formation
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                            <Typography sx={{ color: "white", fontWeight: 'bold', fontSize: '1.2rem' }}>
                                Nom de l'organisme :
                            </Typography>
                            <Typography sx={{ color: "white", fontWeight: 'normal', fontSize: '1.2rem' }}>
                                {organization.name}
                            </Typography>
                            <Typography sx={{ color: "white", fontWeight: 'bold', fontSize: '1.2rem' }}>
                                Subdomain de l'organisme :
                            </Typography>
                            <Typography sx={{ color: "white", fontWeight: 'normal', fontSize: '1.2rem' }}>
                                {organization.subdomain}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => navigate('/dashboard')}
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {saveMessage && (
                <Alert severity={saveMessage.type} sx={{ mb: 3 }}>
                    {saveMessage.text}
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} textColor="inherit" indicatorColor="secondary" sx={{ '& .MuiTab-root': { color: 'white' } }}>
                    <Tab icon={<SettingsIcon />} iconPosition="start" label="Général" />
                    <Tab icon={<LoginIcon />} iconPosition="start" label="Connexion" />
                    <Tab icon={<EmailIcon />} iconPosition="start" label="Emails" />
                </Tabs>
            </Box>

            {/* General Tab */}
            {activeTab === 0 && (
                <Grid container spacing={10}>
                    {/* Logo Section */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={designTokens.glassCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                    Logo de l'organisme
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
                                    {formData.logoUrl ? (
                                        <img
                                            src={formData.logoUrl}
                                            alt="Logo"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: 150,
                                                objectFit: 'contain',
                                                borderRadius: 8,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    ) : (
                                        <Avatar sx={{ width: 120, height: 120, bgcolor: 'primary.main' }}>
                                            <BusinessIcon sx={{ fontSize: 60 }} />
                                        </Avatar>
                                    )}
                                </Box>
                                <TextField
                                    label="URL du logo"
                                    name="logoUrl"
                                    fullWidth
                                    value={formData.logoUrl}
                                    onChange={handleChange}
                                    helperText="Lien vers l'image du logo"
                                    sx={designTokens.input}
                                />
                                <Button variant="outlined" onClick={() => fileInputRef.current?.click()} sx={{ mt: 2 }}>
                                    Téléverser le logo
                                </Button>
                                <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleLogoUpload} />
                                <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                                    Couleurs du thème
                                </Typography>
                                <Box>
                                    <Typography sx={{ marginTop: 2, marginBottom: 2 }} variant="body2" color="text.secondary" gutterBottom>
                                        Couleur principale
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            name="primaryColor"
                                            value={formData.primaryColor}
                                            onChange={handleChange}
                                            style={{
                                                width: 60,
                                                height: 40,
                                                border: 'none',
                                                borderRadius: 8,
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <TextField
                                            name="primaryColor"
                                            value={formData.primaryColor}
                                            onChange={handleChange}
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography sx={{ marginTop: 2, marginBottom: 2 }} variant="body2" color="text.secondary" gutterBottom>
                                        Couleur secondaire
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            name="secondaryColor"
                                            value={formData.secondaryColor}
                                            onChange={handleChange}
                                            style={{
                                                width: 60,
                                                height: 40,
                                                border: 'none',
                                                borderRadius: 8,
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <TextField
                                            name="secondaryColor"
                                            value={formData.secondaryColor}
                                            onChange={handleChange}
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Learner Preview Section */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <LearnerPreview
                            logoUrl={formData.logoUrl}
                            primaryColor={formData.primaryColor}
                            secondaryColor={formData.secondaryColor}
                            orgName={organization.name}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Connection Tab */}
            {activeTab === 1 && (
                <Grid container spacing={10}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={designTokens.glassCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                    Personnalisation Connexion
                                </Typography>

                                {/* Login Logo */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="subtitle2" gutterBottom>Logo de connexion</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        {loginFormData.logoUrl ? (
                                            <img src={loginFormData.logoUrl} alt="Login Logo" style={{ width: 60, height: 60, objectFit: 'contain', borderRadius: 8, border: '1px solid #eee' }} />
                                        ) : (
                                            <Box sx={{ width: 60, height: 60, bgcolor: 'grey.200', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <LoginIcon color="disabled" />
                                            </Box>
                                        )}
                                        <Button variant="outlined" size="small" onClick={() => loginLogoInputRef.current?.click()}>
                                            Changer
                                        </Button>
                                        <input type="file" accept="image/*" ref={loginLogoInputRef} style={{ display: 'none' }} onChange={handleLoginLogoUpload} />
                                    </Box>
                                    <TextField
                                        label="URL du logo"
                                        name="logoUrl"
                                        fullWidth
                                        size="small"
                                        value={loginFormData.logoUrl}
                                        onChange={handleLoginChange}
                                        sx={designTokens.input}
                                    />
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                {/* Background Image */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="subtitle2" gutterBottom>Image de fond</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        {loginFormData.backgroundImageUrl ? (
                                            <img src={loginFormData.backgroundImageUrl} alt="Background" style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
                                        ) : (
                                            <Box sx={{ width: 100, height: 60, bgcolor: 'grey.200', borderRadius: 8 }} />
                                        )}
                                        <Button variant="outlined" size="small" onClick={() => loginBgInputRef.current?.click()}>
                                            Changer
                                        </Button>
                                        <input type="file" accept="image/*" ref={loginBgInputRef} style={{ display: 'none' }} onChange={handleLoginBgUpload} />
                                    </Box>
                                    <TextField
                                        label="URL de l'image de fond"
                                        name="backgroundImageUrl"
                                        fullWidth
                                        size="small"
                                        value={loginFormData.backgroundImageUrl}
                                        onChange={handleLoginChange}
                                        sx={designTokens.input}
                                    />
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                {/* Colors */}
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                                    Couleurs du dégradé
                                </Typography>
                                <Box>
                                    <Typography sx={{ mt: 2, mb: 1 }} variant="body2" color="text.secondary">
                                        Couleur de début
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            name="primaryColor"
                                            value={loginFormData.primaryColor}
                                            onChange={handleLoginChange}
                                            style={{ width: 60, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                        />
                                        <TextField
                                            name="primaryColor"
                                            value={loginFormData.primaryColor}
                                            onChange={handleLoginChange}
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography sx={{ mt: 2, mb: 1 }} variant="body2" color="text.secondary">
                                        Couleur de fin
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <input
                                            type="color"
                                            name="secondaryColor"
                                            value={loginFormData.secondaryColor}
                                            onChange={handleLoginChange}
                                            style={{ width: 60, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                        />
                                        <TextField
                                            name="secondaryColor"
                                            value={loginFormData.secondaryColor}
                                            onChange={handleLoginChange}
                                            size="small"
                                            sx={{ flex: 1 }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                        <LoginPreview
                            logoUrl={loginFormData.logoUrl}
                            backgroundImageUrl={loginFormData.backgroundImageUrl}
                            primaryColor={loginFormData.primaryColor}
                            secondaryColor={loginFormData.secondaryColor}
                            orgName={organization.name}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Email Tab */}
            {activeTab === 2 && (
                <EmailSettingsTab
                    emailSettings={emailSettings}
                    setEmailSettings={setEmailSettings}
                />
            )}

            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 10 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={designTokens.actionButton}
                >
                    Sauvegarder les modifications
                </Button>
            </Grid>
        </Box >
    );
};

export default MyOFPage;
